import React, {createContext, useEffect, useState} from 'react';
import './style.css';
import {EntriesForm} from './entries-form';
import {EntryData, Mode, Operation, Range} from './math';
import {SettingsComponent} from './settings-component';
import {createEntries} from './use-create-entries';
import {defaultSettings} from './use-get-settings';
import {InfoBox} from './info-box';
import {InfoBoxContent} from './info-box-content';
import {i18n} from './i18n';
import {GB, MX, DE} from 'country-flag-icons/react/3x2';

export type Language = 'de' | 'en' | 'es';

export interface Settings {
  readonly operation: Operation;
  readonly mode: Mode;
  readonly range: Range;
  readonly entriesAmount: number;
  readonly baseFactor?: number;
}

export interface FormState {
  validateToggle: boolean | undefined;
  clearFormToggle: boolean;
  regenerateToggle: boolean;
}

export const ValidateContext = createContext<FormState>({
  validateToggle: false,
  clearFormToggle: false,
  regenerateToggle: false,
});

export const LanguageContext = createContext<Language>('de');

export type InfoBoxType = 'mode' | 'amountOfEntries' | 'range' | 'baseFactor';

function App() {
  const [formState, setFormState] = useState<FormState>({
    validateToggle: undefined,
    clearFormToggle: false,
    regenerateToggle: false,
  });

  const [language, setLanguage] = useState<Language>('de');
  const translations = i18n[language];
  const [entries, setEntries] = useState<EntryData[]>([]);
  const [initialRender, setInitialRender] = useState<boolean>(true);
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [regenerate, setRegenerate] = useState(formState.regenerateToggle);

  const [infoBoxType, setInfoBoxType] = useState<InfoBoxType | undefined>(
    undefined
  );

  useEffect(() => {
    if (regenerate !== formState.regenerateToggle || initialRender) {
      setInitialRender(false);
      setRegenerate(formState.regenerateToggle);
      setEntries(createEntries(settings));
    }
  }, [formState.regenerateToggle, initialRender]);

  return (
    <div className="App">
      <LanguageContext.Provider value={language}>
        <InfoBox infoBoxType={infoBoxType}>
          <InfoBoxContent
            infoBoxType={infoBoxType}
            setInfoBoxType={setInfoBoxType}
          />
        </InfoBox>
        <div className={'flex justify-end gap-2 pr-5 pt-2'}>
          {language !== 'de' && (
            <div className={'cursor-pointer'} onClick={() => setLanguage('de')}>
              <DE className={'h-5 w-8'} />
            </div>
          )}
          {language !== 'es' && (
            <div className={'cursor-pointer'} onClick={() => setLanguage('es')}>
              <MX className={'h-5 w-8'} />
            </div>
          )}
          {language !== 'en' && (
            <div className={'cursor-pointer'} onClick={() => setLanguage('en')}>
              <GB className={'h-5 w-8'} />
            </div>
          )}
        </div>
        <ValidateContext.Provider value={formState}>
          <div className="my-0 mx-auto w-11/12 ">
            <h1 className="App-header flex content-center justify-center pb-4 pt-4">
              Mathe
            </h1>
            <SettingsComponent
              settings={settings}
              setSettings={setSettings}
              formState={formState}
              setFormState={setFormState}
              setInfoBoxType={setInfoBoxType}
            />
            <EntriesForm entries={entries} />
            <div className="mb-10 flex flex-row flex-wrap content-center justify-center gap-4 pb-4">
              <button
                type="submit"
                className="rounded border-none text-white hover:opacity-50"
                onClick={() =>
                  setFormState({
                    ...formState,
                    validateToggle: !formState.validateToggle,
                  })
                }
              >
                {translations.buttons.validate}
              </button>
              <button
                type="button"
                className="text-gray rounded border-none hover:opacity-50"
                onClick={() =>
                  setFormState({
                    ...formState,
                    clearFormToggle: !formState.clearFormToggle,
                  })
                }
              >
                {translations.buttons.reset}
              </button>
              <button
                type="button"
                className="text-gray F rounded border-none hover:opacity-50"
                onClick={() =>
                  setFormState({
                    ...formState,
                    regenerateToggle: !formState.regenerateToggle,
                    clearFormToggle: !formState.clearFormToggle,
                  })
                }
              >
                {translations.buttons.regenerate}
              </button>
            </div>
          </div>
        </ValidateContext.Provider>
      </LanguageContext.Provider>
    </div>
  );
}

export default App;
